'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import AppImage from '@/components/ui/AppImage';

// ─── Types ───────────────────────────────────────────────────────────────────
interface HostProfile {
  firstName: string;
  lastName: string;
  phone: string;
  bio: string;
  language: string;
}

interface PropertyDetails {
  type: string;
  title: string;
  description: string;
  location: string;
  city: string;
  country: string;
  bedrooms: number;
  bathrooms: number;
  maxGuests: number;
  amenities: string[];
}

interface PricingDetails {
  pricePerNight: number;
  cleaningFee: number;
  minNights: number;
  instantBook: boolean;
}

// ─── Constants ───────────────────────────────────────────────────────────────
const PROPERTY_TYPES = [
  { id: 'entire_home', label: 'Entire Home', icon: '🏠', desc: 'Guests have the whole place to themselves' },
  { id: 'private_room', label: 'Private Room', icon: '🛏️', desc: 'Guests have their own room in a shared home' },
  { id: 'shared_room', label: 'Shared Room', icon: '🤝', desc: 'Guests sleep in a room or common area you share' },
  { id: 'hotel_room', label: 'Hotel Room', icon: '🏨', desc: 'A room in a boutique hotel or guesthouse' },
  { id: 'villa', label: 'Villa', icon: '🏡', desc: 'A luxurious standalone property with premium amenities' },
  { id: 'apartment', label: 'Apartment', icon: '🏢', desc: 'A self-contained unit in a residential building' },
];

const AMENITIES_LIST = [
  'WiFi', 'Air conditioning', 'Kitchen', 'Parking', 'Pool', 'Gym',
  'TV', 'Washer', 'Dryer', 'Hot tub', 'BBQ grill', 'Garden',
  'Security cameras', 'Smoke alarm', 'First aid kit', 'Fire extinguisher',
];

const STEPS = [
  { id: 1, label: 'Your Profile', icon: '👤' },
  { id: 2, label: 'Property Type', icon: '🏠' },
  { id: 3, label: 'Property Details', icon: '📋' },
  { id: 4, label: 'Pricing', icon: '💰' },
  { id: 5, label: 'Review & Publish', icon: '🚀' },
];

// ─── Landing Hero ─────────────────────────────────────────────────────────────
function HostLanding({ onStart }: { onStart: () => void }) {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-[70vh] min-h-[520px]">
        <AppImage
          src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&q=80"
          alt="Stunning Ugandan villa with lush garden and private pool perfect for hosting guests"
          fill
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 w-full">
            <div className="max-w-xl">
              <span className="inline-block bg-[#4088C6] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-5">
                For Property Owners
              </span>
              <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                Your property.<br />Your income.<br />Your rules.
              </h1>
              <p className="text-white/80 text-lg mb-8 leading-relaxed">
                Join thousands of hosts earning from their spaces across Uganda and East Africa. List for free — no hidden fees.
              </p>
              <button
                onClick={onStart}
                className="bg-[#4088C6] hover:bg-[#3070a6] text-white font-bold px-8 py-4 rounded-2xl text-base transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              >
                Start hosting today →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-gray-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: 'UGX 0', label: 'Listing fee' },
            { value: '14,000+', label: 'Active listings' },
            { value: '200+', label: 'Cities covered' },
            { value: '4.8★', label: 'Avg host rating' },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-2xl font-extrabold text-[#F5A623]">{s.value}</p>
              <p className="text-gray-400 text-sm mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Why host */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-3" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            Why hosts choose Ndoto
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto">Everything you need to run a successful hosting business — from your first listing to your hundredth booking.</p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Large card */}
          <div className="md:col-span-2 relative rounded-3xl overflow-hidden h-72">
            <AppImage
              src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=900&q=80"
              alt="Host managing their property bookings on a laptop in a modern Kampala apartment"
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-[#4088C6] text-xs font-bold uppercase tracking-widest mb-1">Smart Tools</p>
              <h3 className="text-white text-xl font-bold">Manage everything from one dashboard</h3>
              <p className="text-white/70 text-sm mt-1">Reservations, earnings, calendar, and guest messages — all in one place.</p>
            </div>
          </div>

          {/* Tall card */}
          <div className="bg-[#4088C6]/10 rounded-3xl p-7 flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 bg-[#4088C6]/20 rounded-2xl flex items-center justify-center text-2xl mb-4">💸</div>
              <h3 className="text-gray-900 text-lg font-bold mb-2">Earn on your schedule</h3>
              <p className="text-gray-500 text-sm leading-relaxed">Block dates when you need the space. Open it when you want to earn. You're always in control.</p>
            </div>
            <div className="mt-6 bg-white rounded-2xl p-4 shadow-sm">
              <p className="text-xs text-[#4088C6] font-semibold mb-1">Avg monthly earnings</p>
              <p className="text-2xl font-extrabold text-gray-900">UGX 2.4M</p>
              <p className="text-xs text-[#4088C6] font-semibold mt-0.5">↑ 18% vs last quarter</p>
            </div>
          </div>

          {/* Small cards row */}
          <div className="bg-gray-900 rounded-3xl p-7 text-white">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl mb-4">🛡️</div>
            <h3 className="text-lg font-bold mb-2">Host protection</h3>
            <p className="text-white/60 text-sm leading-relaxed">Up to UGX 50M in property damage protection and 24/7 support for every booking.</p>
          </div>

          <div className="bg-indigo-600 rounded-3xl p-7 text-white">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-2xl mb-4">⭐</div>
            <h3 className="text-lg font-bold mb-2">Superhost program</h3>
            <p className="text-white/80 text-sm leading-relaxed">Earn Superhost status for higher visibility, priority support, and exclusive bonuses.</p>
          </div>

          <div className="relative rounded-3xl overflow-hidden h-48">
            <AppImage
              src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=600&q=80"
              alt="Happy host welcoming guests at a beautiful Ugandan guesthouse entrance"
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-5 left-5">
              <p className="text-white font-bold">Verified guests only</p>
              <p className="text-white/70 text-xs">ID-checked & reviewed</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <button
            onClick={onStart}
            className="bg-[#4088C6] hover:bg-[#3070a6] text-white font-bold px-10 py-4 rounded-2xl text-base transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5"
          >
            Get started — it's free
          </button>
          <p className="text-gray-400 text-sm mt-3">Takes about 10 minutes to complete your listing</p>
        </div>
      </div>
    </div>
  );
}

// ─── Step Progress Bar ────────────────────────────────────────────────────────
function StepProgress({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-10 overflow-x-auto pb-2">
      {STEPS.map((step, idx) => (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                step.id < currentStep
                  ? 'bg-[#4088C6] text-white'
                  : step.id === currentStep
                  ? 'bg-[#4088C6] text-white ring-4 ring-[#4088C6]/20' :'bg-gray-100 text-gray-400'
              }`}
            >
              {step.id < currentStep ? '✓' : step.icon}
            </div>
            <span className={`text-xs font-semibold whitespace-nowrap ${step.id === currentStep ? 'text-[#4088C6]' : step.id < currentStep ? 'text-gray-600' : 'text-gray-400'}`}>
              {step.label}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <div className={`h-0.5 w-10 sm:w-16 mx-1 mb-5 transition-all duration-300 ${step.id < currentStep ? 'bg-[#4088C6]' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Step 1: Host Profile ─────────────────────────────────────────────────────
function Step1Profile({ data, onChange }: { data: HostProfile; onChange: (d: HostProfile) => void }) {
  return (
    <div>
      <h2 className="text-2xl font-extrabold text-gray-900 mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Tell guests about yourself</h2>
      <p className="text-gray-500 text-sm mb-8">A complete profile builds trust and helps guests choose your property.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">First name *</label>
          <input
            type="text"
            value={data.firstName}
            onChange={(e) => onChange({ ...data, firstName: e.target.value })}
            placeholder="e.g. Sarah"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Last name *</label>
          <input
            type="text"
            value={data.lastName}
            onChange={(e) => onChange({ ...data, lastName: e.target.value })}
            placeholder="e.g. Nakato"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone number *</label>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => onChange({ ...data, phone: e.target.value })}
            placeholder="+256 700 000 000"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Primary language</label>
          <select
            value={data.language}
            onChange={(e) => onChange({ ...data, language: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all bg-white"
          >
            <option value="English">English</option>
            <option value="Luganda">Luganda</option>
            <option value="Swahili">Swahili</option>
            <option value="French">French</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">About you</label>
          <textarea
            value={data.bio}
            onChange={(e) => onChange({ ...data, bio: e.target.value })}
            placeholder="Tell guests a bit about yourself — your hosting style, what you love about your area, and what makes your space special."
            rows={4}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all resize-none"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Step 2: Property Type ────────────────────────────────────────────────────
function Step2PropertyType({ selected, onSelect }: { selected: string; onSelect: (t: string) => void }) {
  return (
    <div>
      <h2 className="text-2xl font-extrabold text-gray-900 mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>What kind of space are you listing?</h2>
      <p className="text-gray-500 text-sm mb-8">Choose the option that best describes your property.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PROPERTY_TYPES.map((type) => (
          <button
            key={type.id}
            onClick={() => onSelect(type.id)}
            className={`text-left p-5 rounded-2xl border-2 transition-all duration-200 hover:shadow-md ${
              selected === type.id
                ? 'border-rose-500 bg-rose-50 shadow-md'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <span className="text-3xl mb-3 block">{type.icon}</span>
            <p className={`font-bold text-sm mb-1 ${selected === type.id ? 'text-rose-600' : 'text-gray-900'}`}>{type.label}</p>
            <p className="text-gray-500 text-xs leading-relaxed">{type.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 3: Property Details ─────────────────────────────────────────────────
function Step3Details({ data, onChange }: { data: PropertyDetails; onChange: (d: PropertyDetails) => void }) {
  const toggleAmenity = (a: string) => {
    const updated = data.amenities.includes(a)
      ? data.amenities.filter((x) => x !== a)
      : [...data.amenities, a];
    onChange({ ...data, amenities: updated });
  };

  const Counter = ({ label, value, field }: { label: string; value: number; field: 'bedrooms' | 'bathrooms' | 'maxGuests' }) => (
    <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
      <span className="text-sm font-semibold text-gray-700">{label}</span>
      <div className="flex items-center gap-4">
        <button
          onClick={() => onChange({ ...data, [field]: Math.max(1, value - 1) })}
          className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-500 transition-colors font-bold"
        >−</button>
        <span className="w-6 text-center font-bold text-gray-900">{value}</span>
        <button
          onClick={() => onChange({ ...data, [field]: value + 1 })}
          className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-500 transition-colors font-bold"
        >+</button>
      </div>
    </div>
  );

  return (
    <div>
      <h2 className="text-2xl font-extrabold text-gray-900 mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Describe your property</h2>
      <p className="text-gray-500 text-sm mb-8">The more detail you provide, the more bookings you'll attract.</p>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Property title *</label>
          <input
            type="text"
            value={data.title}
            onChange={(e) => onChange({ ...data, title: e.target.value })}
            placeholder="e.g. Serene Garden Villa in Kololo"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description *</label>
          <textarea
            value={data.description}
            onChange={(e) => onChange({ ...data, description: e.target.value })}
            placeholder="Describe your space — what makes it unique, the atmosphere, nearby attractions, and what guests can expect."
            rows={4}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all resize-none"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Street address *</label>
            <input
              type="text"
              value={data.location}
              onChange={(e) => onChange({ ...data, location: e.target.value })}
              placeholder="e.g. 14 Acacia Avenue, Kololo"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">City *</label>
            <input
              type="text"
              value={data.city}
              onChange={(e) => onChange({ ...data, city: e.target.value })}
              placeholder="e.g. Kampala"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Counters */}
        <div className="bg-gray-50 rounded-2xl px-6 py-2">
          <Counter label="Bedrooms" value={data.bedrooms} field="bedrooms" />
          <Counter label="Bathrooms" value={data.bathrooms} field="bathrooms" />
          <Counter label="Max guests" value={data.maxGuests} field="maxGuests" />
        </div>

        {/* Amenities */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Amenities</label>
          <div className="flex flex-wrap gap-2">
            {AMENITIES_LIST.map((a) => (
              <button
                key={a}
                onClick={() => toggleAmenity(a)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-150 ${
                  data.amenities.includes(a)
                    ? 'bg-rose-500 text-white border-rose-500' :'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 4: Pricing ──────────────────────────────────────────────────────────
function Step4Pricing({ data, onChange }: { data: PricingDetails; onChange: (d: PricingDetails) => void }) {
  return (
    <div>
      <h2 className="text-2xl font-extrabold text-gray-900 mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Set your pricing</h2>
      <p className="text-gray-500 text-sm mb-8">You can always adjust your pricing later from your host dashboard.</p>

      <div className="space-y-6">
        {/* Price per night */}
        <div className="bg-rose-50 border border-rose-100 rounded-2xl p-6">
          <label className="block text-sm font-bold text-gray-800 mb-1">Price per night (UGX) *</label>
          <p className="text-xs text-gray-500 mb-4">Similar properties in your area charge UGX 80,000 – 250,000/night</p>
          <div className="flex items-center gap-3">
            <span className="text-gray-500 font-semibold text-sm">UGX</span>
            <input
              type="number"
              value={data.pricePerNight || ''}
              onChange={(e) => onChange({ ...data, pricePerNight: Number(e.target.value) })}
              placeholder="120,000"
              className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-lg font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Cleaning fee (UGX)</label>
            <div className="flex items-center gap-3">
              <span className="text-gray-500 font-semibold text-sm">UGX</span>
              <input
                type="number"
                value={data.cleaningFee || ''}
                onChange={(e) => onChange({ ...data, cleaningFee: Number(e.target.value) })}
                placeholder="20,000"
                className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Minimum nights</label>
            <input
              type="number"
              value={data.minNights}
              onChange={(e) => onChange({ ...data, minNights: Math.max(1, Number(e.target.value)) })}
              min={1}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Instant book toggle */}
        <div className="flex items-center justify-between bg-gray-50 rounded-2xl p-5">
          <div>
            <p className="font-bold text-gray-900 text-sm">Instant Book</p>
            <p className="text-gray-500 text-xs mt-0.5">Let guests book without waiting for your approval. Increases bookings by up to 40%.</p>
          </div>
          <button
            onClick={() => onChange({ ...data, instantBook: !data.instantBook })}
            className={`relative w-12 h-6 rounded-full transition-all duration-200 flex-shrink-0 ${data.instantBook ? 'bg-rose-500' : 'bg-gray-300'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200 ${data.instantBook ? 'left-6' : 'left-0.5'}`} />
          </button>
        </div>

        {/* Earnings estimate */}
        {data.pricePerNight > 0 && (
          <div className="bg-gray-900 text-white rounded-2xl p-6">
            <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Estimated monthly earnings</p>
            <div className="flex items-end gap-2">
              <p className="text-3xl font-extrabold">
                UGX {(data.pricePerNight * 18).toLocaleString()}
              </p>
              <p className="text-gray-400 text-sm mb-1">/ month</p>
            </div>
            <p className="text-gray-500 text-xs mt-2">Based on 18 nights booked at your nightly rate (avg for similar listings)</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 5: Review & Publish ─────────────────────────────────────────────────
function Step5Review({
  profile,
  property,
  pricing,
  onPublish,
}: {
  profile: HostProfile;
  property: PropertyDetails;
  pricing: PricingDetails;
  onPublish: () => void;
}) {
  const typeLabel = PROPERTY_TYPES.find((t) => t.id === property.type)?.label ?? property.type;

  return (
    <div>
      <h2 className="text-2xl font-extrabold text-gray-900 mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Review your listing</h2>
      <p className="text-gray-500 text-sm mb-8">Everything looks good? Hit publish to go live on Ndoto.</p>

      <div className="space-y-5">
        {/* Profile summary */}
        <div className="bg-gray-50 rounded-2xl p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Host Profile</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-500">Name:</span> <span className="font-semibold text-gray-900">{profile.firstName} {profile.lastName}</span></div>
            <div><span className="text-gray-500">Phone:</span> <span className="font-semibold text-gray-900">{profile.phone || '—'}</span></div>
            <div><span className="text-gray-500">Language:</span> <span className="font-semibold text-gray-900">{profile.language}</span></div>
          </div>
          {profile.bio && <p className="text-gray-600 text-sm mt-3 leading-relaxed">{profile.bio}</p>}
        </div>

        {/* Property summary */}
        <div className="bg-gray-50 rounded-2xl p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Property</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-500">Type:</span> <span className="font-semibold text-gray-900">{typeLabel}</span></div>
            <div><span className="text-gray-500">Title:</span> <span className="font-semibold text-gray-900">{property.title || '—'}</span></div>
            <div><span className="text-gray-500">Location:</span> <span className="font-semibold text-gray-900">{property.city || '—'}</span></div>
            <div><span className="text-gray-500">Bedrooms:</span> <span className="font-semibold text-gray-900">{property.bedrooms}</span></div>
            <div><span className="text-gray-500">Bathrooms:</span> <span className="font-semibold text-gray-900">{property.bathrooms}</span></div>
            <div><span className="text-gray-500">Max guests:</span> <span className="font-semibold text-gray-900">{property.maxGuests}</span></div>
          </div>
          {property.amenities.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {property.amenities.map((a) => (
                <span key={a} className="bg-white border border-gray-200 text-gray-600 text-xs px-2.5 py-1 rounded-full">{a}</span>
              ))}
            </div>
          )}
        </div>

        {/* Pricing summary */}
        <div className="bg-gray-50 rounded-2xl p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Pricing</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-500">Per night:</span> <span className="font-semibold text-gray-900">UGX {pricing.pricePerNight.toLocaleString()}</span></div>
            <div><span className="text-gray-500">Cleaning fee:</span> <span className="font-semibold text-gray-900">UGX {pricing.cleaningFee.toLocaleString()}</span></div>
            <div><span className="text-gray-500">Min nights:</span> <span className="font-semibold text-gray-900">{pricing.minNights}</span></div>
            <div><span className="text-gray-500">Instant Book:</span> <span className={`font-semibold ${pricing.instantBook ? 'text-rose-500' : 'text-gray-900'}`}>{pricing.instantBook ? 'Enabled' : 'Disabled'}</span></div>
          </div>
        </div>

        {/* Terms */}
        <div className="bg-rose-50 border border-rose-100 rounded-2xl p-5 text-sm text-gray-600 leading-relaxed">
          By publishing, you agree to Ndoto's <span className="text-rose-500 font-semibold cursor-pointer hover:underline">Host Terms of Service</span> and confirm that your listing complies with local regulations in Uganda.
        </div>

        <button
          onClick={onPublish}
          className="w-full bg-rose-500 hover:bg-rose-600 text-white font-bold py-4 rounded-2xl text-base transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
        >
          🚀 Publish my listing
        </button>
      </div>
    </div>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────
function SuccessScreen() {
  return (
    <div className="text-center py-12 px-4">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">🎉</div>
      <h2 className="text-3xl font-extrabold text-gray-900 mb-3" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
        You're live on Ndoto!
      </h2>
      <p className="text-gray-500 max-w-md mx-auto mb-8 leading-relaxed">
        Your listing is now under review. We'll notify you within 24 hours once it's approved and visible to guests across Africa.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a
          href="/host-dashboard"
          className="bg-[#4088C6] hover:bg-[#2D6FA8] text-white font-bold px-8 py-3.5 rounded-2xl text-sm transition-all duration-200 hover:shadow-lg"
        >
          Go to Host Dashboard
        </a>
        <a
          href="/home"
          className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold px-8 py-3.5 rounded-2xl text-sm transition-all duration-200"
        >
          Back to Home
        </a>
      </div>

      {/* Next steps */}
      <div className="mt-12 max-w-lg mx-auto bg-gray-50 rounded-2xl p-6 text-left">
        <p className="text-sm font-bold text-gray-700 mb-4">What happens next?</p>
        <div className="space-y-3">
          {[
            { icon: '📧', text: 'Check your email for a confirmation and listing ID' },
            { icon: '🔍', text: 'Our team reviews your listing for quality and compliance' },
            { icon: '✅', text: 'Once approved, your property goes live to thousands of guests' },
            { icon: '💰', text: 'Receive payouts directly to your mobile money or bank account' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="text-lg flex-shrink-0">{item.icon}</span>
              <p className="text-sm text-gray-600">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BecomeHostPage() {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [published, setPublished] = useState(false);

  const [profile, setProfile] = useState<HostProfile>({
    firstName: '', lastName: '', phone: '', bio: '', language: 'English',
  });
  const [property, setProperty] = useState<PropertyDetails>({
    type: '', title: '', description: '', location: '', city: '', country: 'Uganda',
    bedrooms: 1, bathrooms: 1, maxGuests: 2, amenities: [],
  });
  const [pricing, setPricing] = useState<PricingDetails>({
    pricePerNight: 0, cleaningFee: 0, minNights: 1, instantBook: false,
  });

  const canProceed = () => {
    if (currentStep === 1) return profile.firstName.trim() && profile.lastName.trim() && profile.phone.trim();
    if (currentStep === 2) return property.type !== '';
    if (currentStep === 3) return property.title.trim() && property.city.trim();
    if (currentStep === 4) return pricing.pricePerNight > 0;
    return true;
  };

  if (!showOnboarding) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-16">
          <HostLanding onStart={() => setShowOnboarding(true)} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {published ? (
            <SuccessScreen />
          ) : (
            <>
              <StepProgress currentStep={currentStep} />

              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7 sm:p-10">
                {currentStep === 1 && <Step1Profile data={profile} onChange={setProfile} />}
                {currentStep === 2 && <Step2PropertyType selected={property.type} onSelect={(t) => setProperty({ ...property, type: t })} />}
                {currentStep === 3 && <Step3Details data={property} onChange={setProperty} />}
                {currentStep === 4 && <Step4Pricing data={pricing} onChange={setPricing} />}
                {currentStep === 5 && (
                  <Step5Review
                    profile={profile}
                    property={property}
                    pricing={pricing}
                    onPublish={() => setPublished(true)}
                  />
                )}

                {/* Navigation buttons */}
                {currentStep < 5 && (
                  <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-100">
                    <button
                      onClick={() => currentStep === 1 ? setShowOnboarding(false) : setCurrentStep(currentStep - 1)}
                      className="text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors px-4 py-2 rounded-xl hover:bg-gray-100"
                    >
                      ← {currentStep === 1 ? 'Back to overview' : 'Back'}
                    </button>
                    <button
                      onClick={() => setCurrentStep(currentStep + 1)}
                      disabled={!canProceed()}
                      className={`px-8 py-3 rounded-2xl text-sm font-bold transition-all duration-200 ${
                        canProceed()
                          ? 'bg-[#4088C6] hover:bg-[#2D6FA8] text-white hover:shadow-lg hover:-translate-y-0.5'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Continue →
                    </button>
                  </div>
                )}
              </div>

              {/* Step indicator */}
              <p className="text-center text-xs text-gray-400 mt-4">Step {currentStep} of {STEPS.length}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
